import { Client as BelfastClient } from '@kaizenplatform/belfast';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { DynamicSchemaField, FileIdsValue, SelectMultipleValue } from '@kaizenplatform/hugot-entity';
import Immutable from 'immutable';

import { demoProjectData } from '../data/demo_project/project';
import {
  DynamicSchemaDefinition,
  DynamicSchemaDefinitionField,
  DynamicSchemaDefinitionSection,
  Project,
} from '../generated/graphql';
import FilesWebApi from '../service/persistent/FilesWebApi';
import {
  AssetCategoryDefinitionForProjectFragment,
  CreativeBriefDefinitionForProjectFragment,
} from './../generated/graphql';
import { filterBlankAssetCategorySectionsField } from './dynamic_schema';

const DEMO_IDS = demoProjectData.map((d) => d.data.project.id);

// TODO: 存在するIDは使われてないよね？
export const isDemo = <P extends Pick<Project, 'id'>>(project: P) => DEMO_IDS.includes(project.id);

export type ProjectForBrief = {
  creativeBriefValue: {
    definition: CreativeBriefDefinitionForProjectFragment;
    values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
  };
  assetCategoryValue?: {
    definition: AssetCategoryDefinitionForProjectFragment;
    values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
  };
};

export const jsonToDynamicSchemaValue = <T>(
  json: any,
  definition: Pick<DynamicSchemaDefinition, 'id'> & {
    sections: Array<
      Pick<DynamicSchemaDefinitionSection, 'id' | 'label' | 'description' | 'meta'> & {
        fields: Array<T>;
      }
    >;
  },
): {
  definition: Pick<DynamicSchemaDefinition, 'id'> & {
    sections: Array<
      Pick<DynamicSchemaDefinitionSection, 'id' | 'label' | 'description' | 'meta'> & {
        fields: Array<T>;
      }
    >;
  };
  values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
} => {
  const fields = definition.sections
    .flatMap((section) => section.fields)
    .map((field) => DynamicSchemaField.fromJSON(field));
  const values = new Map(
    fields.map((field) => {
      const value = field.type === 'SELECT_MULTIPLE' ? Immutable.Set(json[field.key]) : json[field.key];
      return [field.key, field.getAdaptedValue(value)];
    }),
  );
  return { definition, values };
};

export const getProjectForBrief = <
  T extends Pick<Project, 'creativeBriefValues' | 'assetCategoryValues'> & {
    creativeBriefDefinition: CreativeBriefDefinitionForProjectFragment;
    assetCategoryDefinition?: AssetCategoryDefinitionForProjectFragment | null;
  }
>(
  project: T,
) => {
  const projectForBrief: ProjectForBrief & T = {
    ...project,
    creativeBriefValue: jsonToDynamicSchemaValue(project.creativeBriefValues, project.creativeBriefDefinition),
  };
  projectForBrief.assetCategoryValue =
    project.assetCategoryValues &&
    project.assetCategoryDefinition &&
    jsonToDynamicSchemaValue(project.assetCategoryValues, project.assetCategoryDefinition);
  return projectForBrief;
};

export const getBriefFileBucketIds = (
  creativeBriefDefinition: DynamicSchemaDefinition,
  values: Map<string, string | number | boolean | FileIdsValue | SelectMultipleValue | null>,
) => {
  return new Map<string, string>(
    creativeBriefDefinition.sections
      .reduce<DynamicSchemaDefinitionField[]>((fields, section) => {
        return [...fields, ...section.fields];
      }, [])
      .filter((field) => field.type === 'FILES')
      .map((field) => {
        const bucketId = values.get(field.key) as string;
        return [field.key, bucketId];
      }),
  );
};

export const getBriefFiles = async (fileMap: Map<string, string>, belfastClient: BelfastClient) => {
  return new Map<string, FileReference[]>(
    await Promise.all(
      Array.from(fileMap).map(async ([fieldKey, bucketId]) => {
        return belfastClient
          .getAllFilesInBucket(bucketId)
          .then((fileReferences) => [fieldKey, fileReferences] as [string, FileReference[]]);
      }),
    ),
  );
};

export const getAssetFiles = async (
  project: Pick<Project, 'id' | 'bucketId'> &
    Pick<ProjectForBrief, 'assetCategoryValue'> & {
      assetCategoryDefinition?: AssetCategoryDefinitionForProjectFragment;
    },
  belfastClient: BelfastClient,
  filesWebApi: FilesWebApi,
) => {
  let assetFiles: Array<FileReference> = [];
  if (project.bucketId != null) {
    const token = await filesWebApi.fetchToken({ type: 'files', projectId: project.id });
    const { assetCategoryDefinition, assetCategoryValue } = project;
    if (assetCategoryValue != null && assetCategoryDefinition != null) {
      const fileIds = filterBlankAssetCategorySectionsField(assetCategoryDefinition.sections.flatMap((s) => s.fields))
        .filter((field) => field.type == 'FILE_IDS')
        .flatMap((field) => assetCategoryValue.values.get(field.key)) as ReadonlyArray<string>;
      for (const fileId of fileIds) {
        const file = await belfastClient.getFile(fileId, token);
        assetFiles = [...assetFiles, file];
      }
    }
  }
  return assetFiles;
};
